import Tippy from "@tippyjs/react";
import { getReadableIconColor } from "@/utils/entityIcon/getReadableIconColor";
import { resolveBrandSetBg } from "@/utils/entityIcon/resolveBrandSetBg";
import { resolveLogoUrl } from "@/utils/entityIcon/resolveLogoUrl";
import { resolveMarketFlagUrl } from "@/utils/entityIcon/resolveMarketFlagUrl";
import { Z_TOOLTIP } from "@/components/BrandSets/shared/z";
import BrandSetGlyph from "@/components/common/EntityIcon/BrandSetGlyph";
import type { EntityIconEntity } from "@/components/common/EntityIcon/types";

export interface EntityIconProps {
  /** Entity to render — brand or brand set. */
  entity: EntityIconEntity;
  /** Diameter in px. Default 30 (matches most current call sites). */
  size?: number;
  /** Ring color for stacked contexts, rendered as box-shadow. */
  ring?: string;
  /** Match the light gray border used across the app for avatar tiles. */
  withBorder?: boolean;
  /** Wrap in a Tippy "Brand Set" tooltip when the entity is a brand set. No-op for real brands. */
  withTooltip?: boolean;
  className?: string;
}

const GLYPH_RATIO = 0.6;
const LETTER_RATIO = 0.45;

/**
 * Circular entity avatar — one wrapper for brands and brand sets. Dispatches
 * internally so callers stop branching. Physical geometry (radius, size,
 * border, ring) is identical across all three variants.
 */
function EntityIcon({
  entity,
  size = 30,
  ring,
  withBorder = false,
  withTooltip = false,
  className = "",
}: EntityIconProps) {
  const dimension = { width: size, height: size, minWidth: size, minHeight: size };
  const ringStyle = ring ? { boxShadow: `0 0 0 1.5px ${ring}` } : undefined;
  const borderClass = withBorder ? "border border-[#F2F4F7]" : "";

  if (entity?.type === "BRAND_SET") {
    const bg = resolveBrandSetBg(entity.color);
    const iconColor = getReadableIconColor(bg);
    const glyph = Math.round(size * GLYPH_RATIO);
    const tile = (
      <div
        style={{ ...dimension, background: bg, color: iconColor, ...ringStyle }}
        className={`inline-flex items-center justify-center rounded-full shrink-0 ${borderClass} ${className}`}
        title={withTooltip ? undefined : "Brand Set"}
      >
        <BrandSetGlyph width={glyph} height={glyph} />
      </div>
    );
    if (!withTooltip) return tile;
    return (
      <Tippy
        trigger="mouseenter"
        zIndex={Z_TOOLTIP}
        className="!font-normal p-1 !text-[#344054] border border-gray-200 !text-[11px] export-download-container !bg-white"
        arrow={false}
        placement="top"
        offset={[0, 10]}
        content={<span style={{ color: "#344054" }}>Brand Set</span>}
      >
        {tile}
      </Tippy>
    );
  }

  // Market flag — dispatched before `logo` to mirror legacy getIconForEntity,
  // where a non-empty identity always wins. The URL is constructed locally
  // (CDN template / bundled asset), not API data, so it skips resolveLogoUrl.
  const flagUrl = resolveMarketFlagUrl(entity);
  if (flagUrl) {
    return (
      <img
        src={flagUrl}
        alt={entity?.name ?? ""}
        style={{ ...dimension, ...ringStyle }}
        className={`rounded-full object-contain shrink-0 ${borderClass} ${className}`}
      />
    );
  }

  const safeLogo = resolveLogoUrl(entity?.logo);
  if (safeLogo) {
    return (
      <img
        src={safeLogo}
        alt={entity?.name ?? ""}
        style={{ ...dimension, ...ringStyle }}
        className={`rounded-full object-contain bg-white shrink-0 ${borderClass} ${className}`}
      />
    );
  }

  const label = entity?.name ?? entity?.displayName ?? "";
  const initial = label.slice(0, 1).toUpperCase();
  return (
    <div
      style={{ ...dimension, fontSize: Math.round(size * LETTER_RATIO), ...ringStyle }}
      className={`inline-flex items-center justify-center rounded-full bg-[#292D33] text-white font-bold shrink-0 ${borderClass} ${className}`}
    >
      {initial}
    </div>
  );
}

export default EntityIcon;
